import React, { useEffect, useState } from 'react';
import { DollarSign, Users, ShoppingBag, Target, ArrowUpRight, ArrowRight, ChevronDown, Code, Star, MapPin, ExternalLink, Settings2, EyeOff, Eye, ArrowUp, ArrowDown, Plus, Save, Info, BarChart3, LineChart, LayoutGrid, Columns2, Maximize, Smartphone, Monitor, Tablet, FileText, Mail, Bell, TrendingUp, MessageCircle, ThumbsUp, Send, MousePointer, AlertTriangle, CheckCircle, Clock, Zap } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useLanguage } from '../../i18n/LanguageContext';
interface AnalyticsDashboardProps {
  orders: any[];
  users: any[];
  astrologers: any[];
  trackingCodes: any[];
  setActiveTab: (tab: string) => void;
  showTrackingSetup: boolean;
  setShowTrackingSetup: (show: boolean) => void;
  updateTrackingCode: (id: string, field: 'value' | 'enabled', val: string | boolean) => void;
  success: (msg: string) => void;
  selectedPeriod?: 'Сегодня' | 'Вчера' | '7 дней' | '30 дней' | 'Квартал' | 'Год';
}
type WidgetSize = '1/4' | '1/2' | '3/4' | 'full';
type ChartType = 'bar' | 'line';
interface WidgetState {
  id: string;
  visible: boolean;
  order: number;
  size: WidgetSize;
  chartType?: ChartType;
}
// Конфигурация всех возможных виджетов
const WIDGETS_CONFIG = [{
  id: 'tracking',
  title: 'Счётчики и интеграции',
  defaultVisible: true,
  defaultSize: 'full' as WidgetSize
}, {
  id: 'kpi',
  title: 'Ключевые показатели (KPI)',
  defaultVisible: true,
  defaultSize: 'full' as WidgetSize
}, {
  id: 'realtime',
  title: 'Онлайн сейчас',
  defaultVisible: true,
  defaultSize: '1/4' as WidgetSize
}, {
  id: 'alerts',
  title: 'Уведомления и алерты',
  defaultVisible: true,
  defaultSize: '3/4' as WidgetSize
}, {
  id: 'revenue_chart',
  title: 'Выручка по месяцам',
  defaultVisible: true,
  defaultSize: '3/4' as WidgetSize,
  defaultChartType: 'bar' as ChartType
}, {
  id: 'funnel',
  title: 'Воронка конверсии',
  defaultVisible: true,
  defaultSize: '1/4' as WidgetSize
}, {
  id: 'traffic',
  title: 'Источники трафика',
  defaultVisible: true,
  defaultSize: '1/2' as WidgetSize
}, {
  id: 'geography',
  title: 'География пользователей',
  defaultVisible: true,
  defaultSize: '1/2' as WidgetSize
}, {
  id: 'devices',
  title: 'Устройства',
  defaultVisible: true,
  defaultSize: '1/4' as WidgetSize
}, {
  id: 'popular_pages',
  title: 'Популярные страницы',
  defaultVisible: true,
  defaultSize: '3/4' as WidgetSize
}, {
  id: 'services',
  title: 'Заказы по услугам',
  defaultVisible: true,
  defaultSize: '1/2' as WidgetSize
}, {
  id: 'subscriptions',
  title: 'Подписки',
  defaultVisible: true,
  defaultSize: '1/2' as WidgetSize
}, {
  id: 'content',
  title: 'Контент-аналитика',
  defaultVisible: true,
  defaultSize: '1/2' as WidgetSize
}, {
  id: 'reviews_analytics',
  title: 'Отзывы и рейтинг',
  defaultVisible: true,
  defaultSize: '1/2' as WidgetSize
}, {
  id: 'email',
  title: 'Email-рассылки',
  defaultVisible: true,
  defaultSize: '1/2' as WidgetSize
}, {
  id: 'cac',
  title: 'Стоимость привлечения (CAC)',
  defaultVisible: true,
  defaultSize: '1/2' as WidgetSize
}, {
  id: 'astrologers',
  title: 'Топ астрологов',
  defaultVisible: true,
  defaultSize: 'full' as WidgetSize
}, {
  id: 'metrics',
  title: 'Быстрые метрики',
  defaultVisible: true,
  defaultSize: 'full' as WidgetSize
}];
const STORAGE_KEY = 'astriqo_analytics_layout_v3';
export function AnalyticsDashboard({
  orders,
  users,
  astrologers,
  trackingCodes,
  setActiveTab,
  showTrackingSetup,
  setShowTrackingSetup,
  updateTrackingCode,
  success,
  selectedPeriod: globalPeriod
}: AnalyticsDashboardProps) {
  const {
    t
  } = useLanguage();
  // Map global period to analytics period format
  const mapGlobalPeriod = (gp?: string) => {
    switch (gp) {
      case 'Сегодня':
      case 'Вчера':
      case '7 дней':
        return '7d';
      case '30 дней':
        return '30d';
      case 'Квартал':
        return '3m';
      case 'Год':
        return '1y';
      default:
        return '30d';
    }
  };
  const [period, setPeriod] = useState(() => mapGlobalPeriod(globalPeriod));
  // Sync with global period changes
  useEffect(() => {
    if (globalPeriod) {
      setPeriod(mapGlobalPeriod(globalPeriod));
    }
  }, [globalPeriod]);
  const [isEditing, setIsEditing] = useState(false);
  // Состояние макета (порядок, видимость, размер, тип графика)
  const [layout, setLayout] = useState<WidgetState[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate parsed data is an array with valid widget objects
        if (!Array.isArray(parsed) || parsed.length === 0 || !parsed[0]?.id) {
          localStorage.removeItem(STORAGE_KEY);
          throw new Error('Invalid layout data');
        }
        const existingIds = new Set(parsed.map((w: any) => w.id));
        const missing = WIDGETS_CONFIG.filter(w => !existingIds.has(w.id)).map((w, i) => ({
          id: w.id,
          visible: w.defaultVisible,
          order: parsed.length + i,
          size: w.defaultSize,
          chartType: w.defaultChartType
        }));
        return [...parsed, ...missing].sort((a, b) => a.order - b.order);
      }
    } catch (e) {
      localStorage.removeItem(STORAGE_KEY);
    }
    // Дефолтный макет
    return WIDGETS_CONFIG.map((w, i) => ({
      id: w.id,
      visible: w.defaultVisible,
      order: i,
      size: w.defaultSize,
      chartType: w.defaultChartType
    }));
  });
  // Сохранение макета при изменении
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  }, [layout]);
  // Функции управления макетом
  const moveWidget = (index: number, direction: 'up' | 'down') => {
    const newLayout = [...layout];
    if (direction === 'up' && index > 0) {
      const temp = newLayout[index].order;
      newLayout[index].order = newLayout[index - 1].order;
      newLayout[index - 1].order = temp;
    } else if (direction === 'down' && index < newLayout.length - 1) {
      const temp = newLayout[index].order;
      newLayout[index].order = newLayout[index + 1].order;
      newLayout[index + 1].order = temp;
    }
    setLayout(newLayout.sort((a, b) => a.order - b.order));
  };
  const toggleVisibility = (id: string) => {
    setLayout(layout.map(w => w.id === id ? {
      ...w,
      visible: !w.visible
    } : w));
  };
  const changeSize = (id: string, size: WidgetSize) => {
    setLayout(layout.map(w => w.id === id ? {
      ...w,
      size
    } : w));
  };
  const toggleChartType = (id: string) => {
    setLayout(layout.map(w => w.id === id ? {
      ...w,
      chartType: w.chartType === 'bar' ? 'line' : 'bar'
    } : w));
  };
  const resetLayout = () => {
    const defaultLayout = WIDGETS_CONFIG.map((w, i) => ({
      id: w.id,
      visible: w.defaultVisible,
      order: i,
      size: w.defaultSize,
      chartType: w.defaultChartType
    }));
    setLayout(defaultLayout);
    localStorage.removeItem(STORAGE_KEY);
    success('Макет дашборда сброшен к настройкам по умолчанию');
  };
  // --- ВЫЧИСЛЕНИЯ ДАННЫХ ---
  const totalRevenue = orders.reduce((sum, o) => sum + parseInt(o.amount.replace(/[^\d]/g, '')), 0);
  const activeUsers = users.filter(u => u.status === 'active').length;
  const totalOrders = orders.length;
  const avgOrderValue = Math.round(totalRevenue / totalOrders) || 0;
  const completedOrders = orders.filter(o => o.status === 'completed').length;
  const conversionRate = totalOrders ? Math.round(completedOrders / totalOrders * 100) : 0;
  const proUsers = users.filter(u => u.subscription === 'Pro').length;
  const plusUsers = users.filter(u => u.subscription === 'Plus').length;
  const freeUsers = users.filter(u => !u.subscription).length;
  const serviceBreakdown = orders.reduce((acc: Record<string, number>, o) => {
    acc[o.service] = (acc[o.service] || 0) + 1;
    return acc;
  }, {});
  const topAstrologersList = [...astrologers].filter(a => a.status === 'active' && a.verified).sort((a, b) => b.orders - a.orders).slice(0, 5);
  const multiplier = period === '7d' ? 0.25 : period === '30d' ? 1 : period === '3m' ? 2.8 : 10.5;
  const monthlyData = [{
    month: 'Окт',
    revenue: 285000 * multiplier
  }, {
    month: 'Ноя',
    revenue: 312000 * multiplier
  }, {
    month: 'Дек',
    revenue: 398000 * multiplier
  }, {
    month: 'Янв',
    revenue: 345000 * multiplier
  }, {
    month: 'Фев',
    revenue: 421000 * multiplier
  }, {
    month: 'Мар',
    revenue: 478000 * multiplier
  }];
  const maxRevenue = Math.max(...monthlyData.map(d => d.revenue));
  const trafficSources = [{
    source: 'Органический поиск (SEO)',
    visits: Math.round(12400 * multiplier),
    percent: 35,
    color: 'bg-blue-500'
  }, {
    source: 'Telegram-каналы',
    visits: Math.round(8200 * multiplier),
    percent: 23,
    color: 'bg-sky-500'
  }, {
    source: 'VK Реклама',
    visits: Math.round(5100 * multiplier),
    percent: 14,
    color: 'bg-indigo-500'
  }, {
    source: 'Прямые заходы',
    visits: Math.round(4200 * multiplier),
    percent: 12,
    color: 'bg-slate-500'
  }, {
    source: 'Instagram / Reels',
    visits: Math.round(3100 * multiplier),
    percent: 9,
    color: 'bg-pink-500'
  }, {
    source: 'Яндекс.Дзен',
    visits: Math.round(1800 * multiplier),
    percent: 5,
    color: 'bg-amber-500'
  }, {
    source: 'YouTube',
    visits: Math.round(700 * multiplier),
    percent: 2,
    color: 'bg-red-500'
  }];
  const funnel = [{
    stage: 'Посетители',
    value: Math.round(35500 * multiplier),
    percent: 100
  }, {
    stage: 'Регистрации',
    value: Math.round(8400 * multiplier),
    percent: 23.6
  }, {
    stage: 'Начали оформление',
    value: Math.round(3200 * multiplier),
    percent: 9.0
  }, {
    stage: 'Успешные оплаты',
    value: Math.round(1450 * multiplier),
    percent: 4.1
  }];
  const geography = [{
    city: 'Москва',
    users: Math.round(4500 * multiplier),
    percent: 35
  }, {
    city: 'Санкт-Петербург',
    users: Math.round(2100 * multiplier),
    percent: 16
  }, {
    city: 'Екатеринбург',
    users: Math.round(850 * multiplier),
    percent: 6
  }, {
    city: 'Краснодар',
    users: Math.round(620 * multiplier),
    percent: 5
  }, {
    city: 'Новосибирск',
    users: Math.round(540 * multiplier),
    percent: 4
  }, {
    city: 'Остальные',
    users: Math.round(3890 * multiplier),
    percent: 34
  }];
  const connectedCount = trackingCodes.filter(t => t.value.trim() && t.enabled).length;
  // --- НОВЫЕ ДАННЫЕ ДЛЯ ВИДЖЕТОВ ---
  const onlineNow = Math.round(23 + Math.random() * 15);
  const deviceData = [{
    device: 'Мобильные',
    percent: 62,
    icon: Smartphone,
    color: 'bg-blue-500',
    textColor: 'text-blue-600',
    bgColor: 'bg-blue-50'
  }, {
    device: 'Десктоп',
    percent: 31,
    icon: Monitor,
    color: 'bg-indigo-500',
    textColor: 'text-indigo-600',
    bgColor: 'bg-indigo-50'
  }, {
    device: 'Планшеты',
    percent: 7,
    icon: Tablet,
    color: 'bg-amber-500',
    textColor: 'text-amber-600',
    bgColor: 'bg-amber-50'
  }];
  const popularPages = [{
    page: 'Главная',
    path: '/',
    views: Math.round(18200 * multiplier),
    percent: 100
  }, {
    page: 'Каталог астрологов',
    path: '/astrologers',
    views: Math.round(8400 * multiplier),
    percent: 46
  }, {
    page: 'Ежедневный гороскоп',
    path: '/daily',
    views: Math.round(7100 * multiplier),
    percent: 39
  }, {
    page: 'Натальная карта',
    path: '/natal',
    views: Math.round(5300 * multiplier),
    percent: 29
  }, {
    page: 'Блог',
    path: '/blog',
    views: Math.round(4200 * multiplier),
    percent: 23
  }, {
    page: 'Тарифы',
    path: '/pricing',
    views: Math.round(3100 * multiplier),
    percent: 17
  }, {
    page: 'Совместимость',
    path: '/compatibility',
    views: Math.round(2800 * multiplier),
    percent: 15
  }];
  const blogStats = {
    totalViews: Math.round(28500 * multiplier),
    avgReadTime: '3:45',
    topArticles: [{
      title: 'Ретроградный Меркурий 2025',
      views: Math.round(4200 * multiplier),
      comments: 47
    }, {
      title: 'Натальная карта: полный гайд',
      views: Math.round(3800 * multiplier),
      comments: 32
    }, {
      title: 'Совместимость знаков',
      views: Math.round(3100 * multiplier),
      comments: 28
    }, {
      title: 'Лунный календарь на март',
      views: Math.round(2700 * multiplier),
      comments: 19
    }]
  };
  const reviewsData = {
    avgRating: 4.7,
    totalReviews: Math.round(842 * multiplier),
    positive: 89,
    neutral: 8,
    negative: 3,
    recentTrend: '+0.2',
    distribution: [{
      stars: 5,
      percent: 72
    }, {
      stars: 4,
      percent: 17
    }, {
      stars: 3,
      percent: 6
    }, {
      stars: 2,
      percent: 3
    }, {
      stars: 1,
      percent: 2
    }]
  };
  const emailStats = {
    sent: Math.round(12400 * multiplier),
    opened: Math.round(4960 * multiplier),
    clicked: Math.round(1488 * multiplier),
    unsubscribed: Math.round(62 * multiplier),
    openRate: 40,
    clickRate: 12,
    campaigns: [{
      name: 'Еженедельный гороскоп',
      openRate: 52,
      clickRate: 18
    }, {
      name: 'Промо: скидка 30%',
      openRate: 38,
      clickRate: 15
    }, {
      name: 'Новый астролог',
      openRate: 35,
      clickRate: 9
    }]
  };
  const cacData = [{
    channel: 'SEO (органика)',
    cac: 0,
    orders: Math.round(420 * multiplier),
    color: 'bg-emerald-500'
  }, {
    channel: 'Telegram',
    cac: 320,
    orders: Math.round(180 * multiplier),
    color: 'bg-sky-500'
  }, {
    channel: 'VK Реклама',
    cac: 580,
    orders: Math.round(95 * multiplier),
    color: 'bg-indigo-500'
  }, {
    channel: 'Instagram',
    cac: 450,
    orders: Math.round(72 * multiplier),
    color: 'bg-pink-500'
  }, {
    channel: 'Яндекс.Директ',
    cac: 720,
    orders: Math.round(45 * multiplier),
    color: 'bg-amber-500'
  }];
  const avgCAC = Math.round(cacData.filter(c => c.cac > 0).reduce((s, c) => s + c.cac, 0) / cacData.filter(c => c.cac > 0).length);
  const alertsData = [{
    type: 'warning',
    icon: AlertTriangle,
    title: 'Рост отказов на мобильных',
    desc: 'Показатель отказов на мобильных вырос на 8% за последние 3 дня',
    time: '2 часа назад',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200'
  }, {
    type: 'success',
    icon: CheckCircle,
    title: 'Рекорд выручки',
    desc: 'Вчерашняя выручка (87 400 ₽) — максимум за последний месяц',
    time: '5 часов назад',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200'
  }, {
    type: 'info',
    icon: Zap,
    title: 'Всплеск трафика из Telegram',
    desc: 'Переходы из Telegram выросли на 340% — проверьте источник',
    time: '1 день назад',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200'
  }, {
    type: 'warning',
    icon: Clock,
    title: 'Долгие ответы астрологов',
    desc: 'Среднее время ответа выросло до 18 часов (норма: 12ч)',
    time: '1 день назад',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200'
  }];
  // --- РЕНДЕР ВИДЖЕТОВ ---
  // Обертка для виджета (добавляет контролы редактирования)
  const WidgetWrapper = ({
    widget,
    index,
    children
  }: {
    widget: WidgetState;
    index: number;
    children: React.ReactNode;
  }) => {
    const sizeClasses = {
      '1/4': 'md:col-span-1',
      '1/2': 'md:col-span-2',
      '3/4': 'md:col-span-3',
      full: 'md:col-span-4'
    };
    const className = `w-full ${sizeClasses[widget.size]} transition-all duration-300`;
    if (!isEditing) return <div className={className} data-id="element-9897">{children}</div>;
    return <div className={`relative group ${className}`} data-id="element-9898">
        <div className="absolute -inset-2 border-2 border-indigo-400 border-dashed rounded-xl z-20 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-9899" />

        {/* Панель управления виджетом */}
        <div className="absolute -top-4 -right-2 z-30 flex items-center gap-1 bg-white shadow-lg rounded-lg p-1 border border-slate-200 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-9900">
          {/* Выбор размера */}
          <div className="flex items-center bg-slate-50 rounded-md p-0.5 mr-1 border border-slate-200" data-id="element-9901">
            <button onClick={() => changeSize(widget.id, '1/4')} className={`p-1 rounded text-xs font-bold ${widget.size === '1/4' ? 'bg-white shadow text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`} title="25% ширины" data-id="element-9902">
              25%
            </button>
            <button onClick={() => changeSize(widget.id, '1/2')} className={`p-1 rounded text-xs font-bold ${widget.size === '1/2' ? 'bg-white shadow text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`} title="50% ширины" data-id="element-9903">
              50%
            </button>
            <button onClick={() => changeSize(widget.id, '3/4')} className={`p-1 rounded text-xs font-bold ${widget.size === '3/4' ? 'bg-white shadow text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`} title="75% ширины" data-id="element-9904">
              75%
            </button>
            <button onClick={() => changeSize(widget.id, 'full')} className={`p-1 rounded text-xs font-bold ${widget.size === 'full' ? 'bg-white shadow text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`} title="100% ширины" data-id="element-9905">
              100%
            </button>
          </div>

          {/* Тип графика (если применимо) */}
          {widget.id === 'revenue_chart' && <div className="flex items-center bg-slate-50 rounded-md p-0.5 mr-1 border border-slate-200" data-id="element-9906">
              <button onClick={() => toggleChartType(widget.id)} className={`p-1 rounded ${widget.chartType === 'bar' ? 'bg-white shadow text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`} title="Столбцы" data-id="element-9907">
                <BarChart3 className="w-4 h-4" data-id="element-9908" />
              </button>
              <button onClick={() => toggleChartType(widget.id)} className={`p-1 rounded ${widget.chartType === 'line' ? 'bg-white shadow text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`} title="Линия" data-id="element-9909">
                <LineChart className="w-4 h-4" data-id="element-9910" />
              </button>
            </div>}

          <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-9911" />

          {/* Перемещение */}
          <button onClick={() => moveWidget(index, 'up')} disabled={index === 0} className="p-1.5 text-slate-500 hover:bg-slate-100 hover:text-indigo-600 rounded-md disabled:opacity-30 disabled:hover:bg-transparent" title="Вверх" data-id="element-9912">
            <ArrowUp className="w-4 h-4" data-id="element-9913" />
          </button>
          <button onClick={() => moveWidget(index, 'down')} disabled={index === layout.length - 1} className="p-1.5 text-slate-500 hover:bg-slate-100 hover:text-indigo-600 rounded-md disabled:opacity-30 disabled:hover:bg-transparent" title="Вниз" data-id="element-9914">
            <ArrowDown className="w-4 h-4" data-id="element-9915" />
          </button>

          <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-9916" />

          {/* Скрытие */}
          <button onClick={() => toggleVisibility(widget.id)} className="p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600 rounded-md" title="Скрыть виджет" data-id="element-9917">
            <EyeOff className="w-4 h-4" data-id="element-9918" />
          </button>
        </div>
        <div className="pointer-events-none opacity-50" data-id="element-9919">{children}</div>
      </div>;
  };
  const renderWidget = (widget: WidgetState) => {
    switch (widget.id) {
      case 'tracking':
        return <Card padding="none" className="overflow-hidden border-slate-200 h-full" data-id="element-9920">
            <button onClick={() => setShowTrackingSetup(!showTrackingSetup)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-9921">
              <div className="flex items-center gap-3" data-id="element-9922">
                <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center" data-id="element-9923">
                  <Code className="w-4.5 h-4.5 text-violet-600" data-id="element-9924" />
                </div>
                <div className="text-left" data-id="element-9925">
                  <h3 className="text-sm font-bold text-slate-900" data-id="element-9926">
                    Счётчики и интеграции
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-9927">
                    {connectedCount > 0 ? `Подключено: ${connectedCount} из ${trackingCodes.length}` : 'Добавьте коды аналитики для отслеживания трафика'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3" data-id="element-9928">
                {connectedCount > 0 && <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200" data-id="element-9929">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-9930" />
                    <span className="text-[10px] font-bold text-emerald-700" data-id="element-9931">
                      {connectedCount} активно
                    </span>
                  </div>}
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${showTrackingSetup ? 'rotate-180' : ''}`} data-id="element-9932" />
              </div>
            </button>

            {showTrackingSetup && <div className="border-t border-slate-100 px-5 py-4" data-id="element-9933">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-9934">
                  {trackingCodes.map(tracker => <div key={tracker.id} className={`rounded-xl border p-4 transition-all ${tracker.value.trim() && tracker.enabled ? tracker.color : 'bg-white border-slate-200'}`} data-id="element-9935">
                      <div className="flex items-center justify-between mb-3" data-id="element-9936">
                        <div className="flex items-center gap-2.5" data-id="element-9937">
                          <span className="text-lg" data-id="element-9938">{tracker.icon}</span>
                          <div data-id="element-9939">
                            <div className="text-xs font-bold text-slate-900" data-id="element-9940">
                              {tracker.name}
                            </div>
                            <div className="text-[10px] text-slate-500" data-id="element-9941">
                              {tracker.description}
                            </div>
                          </div>
                        </div>
                        <button className={`w-8 h-5 rounded-full transition-colors ${tracker.enabled && tracker.value.trim() ? 'bg-emerald-500' : 'bg-slate-300'} relative`} onClick={() => {
                    if (!tracker.value.trim() && !tracker.enabled) {
                      success('Сначала введите ID счётчика');
                      return;
                    }
                    updateTrackingCode(tracker.id, 'enabled', !tracker.enabled);
                    success(tracker.enabled ? `${tracker.name} отключён` : `${tracker.name} подключён`);
                  }} data-id="element-9942">
                          <div className={`w-3.5 h-3.5 rounded-full bg-white absolute top-0.5 transition-transform ${tracker.enabled && tracker.value.trim() ? 'translate-x-3.5' : 'translate-x-0.5'}`} data-id="element-9943" />
                        </button>
                      </div>
                      {tracker.id === 'custom_head' ? <textarea value={tracker.value} onChange={e => updateTrackingCode(tracker.id, 'value', e.target.value)} placeholder={tracker.placeholder} rows={2} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs font-mono outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 transition-all resize-none bg-white/80" data-id="element-9944" /> : <input type="text" value={tracker.value} onChange={e => updateTrackingCode(tracker.id, 'value', e.target.value)} placeholder={tracker.placeholder} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs font-mono outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 transition-all bg-white/80" data-id="element-9945" />}
                    </div>)}
                </div>
                <div className="mt-4 flex items-center justify-between" data-id="element-9946">
                  <p className="text-[10px] text-slate-400" data-id="element-9947">
                    Коды сохраняются автоматически. Включите переключатель,
                    чтобы активировать счётчик.
                  </p>
                  <Button variant="primary" size="sm" className="h-7 text-[11px] px-3" onClick={() => {
                const active = trackingCodes.filter(t => t.value.trim() && t.enabled);
                if (active.length === 0) {
                  success('Нет активных счётчиков. Введите ID и включите переключатель.');
                } else {
                  success(`Сохранено: ${active.map(t => t.name).join(', ')}`);
                }
              }} data-id="element-9948">
                    Проверить подключение
                  </Button>
                </div>
              </div>}
          </Card>;
      case 'kpi':
        return <div className={`grid grid-cols-2 gap-4 ${widget.size === 'full' ? 'lg:grid-cols-4' : widget.size === '3/4' ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`} data-id="element-9949">
            <Card padding="md" className="relative overflow-hidden group hover:shadow-md transition-all h-full" data-id="element-9950">
              <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-50 rounded-full -mr-4 -mt-4 transition-transform group-hover:scale-110" data-id="element-9951" />
              <div className="relative" data-id="element-9952">
                <div className="flex items-center justify-between mb-2" data-id="element-9953">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center" data-id="element-9954">
                    <DollarSign className="w-4 h-4 text-emerald-600" data-id="element-9955" />
                  </div>
                  <button onClick={() => setActiveTab('finance')} className="text-[10px] font-medium text-slate-500 hover:text-emerald-600 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-9956">
                    В финансы <ArrowRight className="w-3 h-3" data-id="element-9957" />
                  </button>
                </div>
                <div className="text-2xl font-bold text-slate-900" data-id="element-9958">
                  {(totalRevenue / 1000).toFixed(0)}K ₽
                </div>
                <div className="text-xs text-slate-500 mt-1" data-id="element-9959">Общая выручка</div>
                <div className="flex items-center gap-1 mt-2 text-xs text-emerald-600 font-medium" data-id="element-9960">
                  <ArrowUpRight className="w-3 h-3" data-id="element-9961" /> +18.2%
                </div>
              </div>
            </Card>

            <Card padding="md" className="relative overflow-hidden group hover:shadow-md transition-all h-full" data-id="element-9962">
              <div className="absolute top-0 right-0 w-16 h-16 bg-blue-50 rounded-full -mr-4 -mt-4 transition-transform group-hover:scale-110" data-id="element-9963" />
              <div className="relative" data-id="element-9964">
                <div className="flex items-center justify-between mb-2" data-id="element-9965">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center" data-id="element-9966">
                    <Users className="w-4 h-4 text-blue-600" data-id="element-9967" />
                  </div>
                  <button onClick={() => setActiveTab('users')} className="text-[10px] font-medium text-slate-500 hover:text-blue-600 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-9968">
                    Пользователи <ArrowRight className="w-3 h-3" data-id="element-9969" />
                  </button>
                </div>
                <div className="text-2xl font-bold text-slate-900" data-id="element-9970">
                  {activeUsers}
                </div>
                <div className="text-xs text-slate-500 mt-1" data-id="element-9971">
                  Активных пользователей
                </div>
                <div className="flex items-center gap-1 mt-2 text-xs text-emerald-600 font-medium" data-id="element-9972">
                  <ArrowUpRight className="w-3 h-3" data-id="element-9973" /> +12.5%
                </div>
              </div>
            </Card>

            <Card padding="md" className="relative overflow-hidden group hover:shadow-md transition-all h-full" data-id="element-9974">
              <div className="absolute top-0 right-0 w-16 h-16 bg-indigo-50 rounded-full -mr-4 -mt-4 transition-transform group-hover:scale-110" data-id="element-9975" />
              <div className="relative" data-id="element-9976">
                <div className="flex items-center justify-between mb-2" data-id="element-9977">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center" data-id="element-9978">
                    <ShoppingBag className="w-4 h-4 text-indigo-600" data-id="element-9979" />
                  </div>
                  <button onClick={() => setActiveTab('orders')} className="text-[10px] font-medium text-slate-500 hover:text-indigo-600 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-9980">
                    Все заказы <ArrowRight className="w-3 h-3" data-id="element-9981" />
                  </button>
                </div>
                <div className="text-2xl font-bold text-slate-900" data-id="element-9982">
                  {totalOrders}
                </div>
                <div className="text-xs text-slate-500 mt-1" data-id="element-9983">Всего заказов</div>
                <div className="flex items-center gap-1 mt-2 text-xs text-emerald-600 font-medium" data-id="element-9984">
                  <ArrowUpRight className="w-3 h-3" data-id="element-9985" /> +8.3%
                </div>
              </div>
            </Card>

            <Card padding="md" className="relative overflow-hidden group hover:shadow-md transition-all h-full" data-id="element-9986">
              <div className="absolute top-0 right-0 w-16 h-16 bg-amber-50 rounded-full -mr-4 -mt-4 transition-transform group-hover:scale-110" data-id="element-9987" />
              <div className="relative" data-id="element-9988">
                <div className="flex items-center justify-between mb-2" data-id="element-9989">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center" data-id="element-9990">
                    <Target className="w-4 h-4 text-amber-600" data-id="element-9991" />
                  </div>
                  <button onClick={() => setActiveTab('orders')} className="text-[10px] font-medium text-slate-500 hover:text-amber-600 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-9992">
                    Все заказы <ArrowRight className="w-3 h-3" data-id="element-9993" />
                  </button>
                </div>
                <div className="text-2xl font-bold text-slate-900" data-id="element-9994">
                  {conversionRate}%
                </div>
                <div className="text-xs text-slate-500 mt-1" data-id="element-9995">
                  Конверсия в заказ
                </div>
                <div className="flex items-center gap-1 mt-2 text-xs text-emerald-600 font-medium" data-id="element-9996">
                  <ArrowUpRight className="w-3 h-3" data-id="element-9997" /> +2.1%
                </div>
              </div>
            </Card>
          </div>;
      case 'revenue_chart':
        return <Card padding="md" className="flex flex-col h-full min-h-[300px]" data-id="element-9998">
            <div className="flex items-center justify-between mb-6" data-id="element-9999">
              <div data-id="element-10000">
                <h3 className="text-sm font-bold text-slate-900" data-id="element-10001">
                  Выручка по месяцам
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-10002">
                  Динамика за последние полгода
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-10003">
                <Badge variant="success" size="sm" data-id="element-10004">
                  +67.7% за период
                </Badge>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('finance')} data-id="element-10005">
                  Детализация
                </Button>
              </div>
            </div>

            <div className="flex-1 mt-auto relative" data-id="element-10006">
              {widget.chartType === 'line' ?
            // Линейный график (SVG)
            <div className="absolute inset-0 flex items-end" data-id="element-10007">
                  <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-10008">
                    {/* Линии сетки */}
                    {[0, 0.25, 0.5, 0.75, 1].map((line, i) => <line key={i} x1="0" y1={line * 100} x2="100" y2={line * 100} stroke="#f1f5f9" strokeWidth="0.5" data-id="element-10009" />)}

                    {/* Сама линия графика */}
                    <polyline fill="none" stroke="#6366f1" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" points={monthlyData.map((d, i) => {
                  const x = i / (monthlyData.length - 1) * 100;
                  const y = 100 - d.revenue / maxRevenue * 100;
                  return `${x},${y}`;
                }).join(' ')} data-id="element-10010" />

                    {/* Точки на линии */}
                    {monthlyData.map((d, i) => {
                  const x = i / (monthlyData.length - 1) * 100;
                  const y = 100 - d.revenue / maxRevenue * 100;
                  return <circle key={i} cx={x} cy={y} r="2" fill="white" stroke="#6366f1" strokeWidth="1" data-id="element-10011" />;
                })}
                  </svg>

                  {/* Подписи оси X */}
                  <div className="absolute -bottom-6 left-0 right-0 flex justify-between" data-id="element-10012">
                    {monthlyData.map((d, i) => <div key={i} className="text-[10px] text-slate-500 font-medium text-center" style={{
                  width: '20px',
                  marginLeft: '-10px'
                }} data-id="element-10013">
                        {d.month}
                      </div>)}
                  </div>
                </div> :
            // Столбчатый график
            <div className="flex items-end gap-3 h-full" data-id="element-10014">
                  {monthlyData.map((d, i) => <div key={i} className="flex-1 flex flex-col items-center gap-2 group h-full justify-end" data-id="element-10015">
                      <div className="text-[10px] font-bold text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-10016">
                        {(d.revenue / 1000).toFixed(0)}K
                      </div>
                      <div className="w-full bg-indigo-100 rounded-t-lg relative overflow-hidden transition-all duration-500 group-hover:bg-indigo-200" style={{
                  height: `${d.revenue / maxRevenue * 100}%`
                }} data-id="element-10017">
                        <div className="absolute inset-0 bg-gradient-to-t from-indigo-500 to-indigo-400 rounded-t-lg" data-id="element-10018" />
                      </div>
                      <div className="text-[10px] text-slate-500 font-medium" data-id="element-10019">
                        {d.month}
                      </div>
                    </div>)}
                </div>}
            </div>
          </Card>;
      case 'funnel':
        return <Card padding="md" className="h-full" data-id="element-10020">
            <div className="flex items-center justify-between mb-6" data-id="element-10021">
              <div data-id="element-10022">
                <h3 className="text-sm font-bold text-slate-900" data-id="element-10023">
                  Воронка конверсии
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-10024">
                  Путь пользователя
                </p>
              </div>
              <Button variant="ghost" size="sm" className="px-2" onClick={() => setActiveTab('orders')} data-id="element-10025">
                <ExternalLink className="w-4 h-4 text-slate-400" data-id="element-10026" />
              </Button>
            </div>
            <div className="space-y-4" data-id="element-10027">
              {funnel.map((step, i) => <div key={i} className="relative" data-id="element-10028">
                  <div className="flex justify-between text-xs mb-1.5 relative z-10" data-id="element-10029">
                    <span className="font-medium text-slate-700" data-id="element-10030">
                      {step.stage}
                    </span>
                    <div className="text-right" data-id="element-10031">
                      <span className="font-bold text-slate-900 block" data-id="element-10032">
                        {step.value.toLocaleString('ru-RU')}
                      </span>
                      <span className="text-[10px] text-slate-500" data-id="element-10033">
                        {step.percent}%
                      </span>
                    </div>
                  </div>
                  <div className="h-8 bg-slate-50 rounded-lg overflow-hidden relative border border-slate-100" data-id="element-10034">
                    <div className="h-full bg-emerald-100/50 absolute left-0 top-0 transition-all duration-1000" style={{
                  width: `${step.percent}%`
                }} data-id="element-10035" />
                    <div className="h-full bg-emerald-400 absolute left-0 top-0 transition-all duration-1000" style={{
                  width: '4px'
                }} data-id="element-10036" />
                  </div>
                  {i < funnel.length - 1 && <div className="absolute -bottom-3 left-4 w-0.5 h-3 bg-slate-200" data-id="element-10037" />}
                </div>)}
            </div>
          </Card>;
      case 'traffic':
        return <Card padding="md" className="h-full" data-id="element-10038">
            <div className="flex items-center justify-between mb-6" data-id="element-10039">
              <div className="flex items-center gap-2" data-id="element-10040">
                <div data-id="element-10041">
                  <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5" data-id="element-10042">
                    Источники трафика
                    <div className="group relative" data-id="element-10043">
                      <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" data-id="element-10044" />
                      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-slate-800 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center" data-id="element-10045">
                        Данные собираются автоматически из подключенных
                        счётчиков и UTM-меток заказов.
                        <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-slate-800" data-id="element-10046" />
                      </div>
                    </div>
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-10047">
                    Откуда приходят пользователи
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setActiveTab('marketing')} data-id="element-10048">
                Настройки трекинга
              </Button>
            </div>
            <div className={`grid gap-x-8 gap-y-4 ${widget.size === '1/4' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`} data-id="element-10049">
              {trafficSources.map((src, i) => <div key={src.source} className="flex items-center gap-3 group" data-id="element-10050">
                  <div className={`w-2.5 h-2.5 rounded-full ${src.color} flex-shrink-0`} data-id="element-10051" />
                  <div className="flex-1 min-w-0" data-id="element-10052">
                    <div className="flex justify-between text-xs mb-1" data-id="element-10053">
                      <span className="font-medium text-slate-700 truncate" data-id="element-10054">
                        {src.source}
                      </span>
                      <span className="text-slate-500 ml-2 flex-shrink-0" data-id="element-10055">
                        {src.visits.toLocaleString('ru-RU')}
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-10056">
                      <div className={`h-full ${src.color} rounded-full transition-all duration-1000`} style={{
                    width: `${src.percent}%`
                  }} data-id="element-10057" />
                    </div>
                  </div>
                  <span className="text-[10px] font-bold text-slate-500 w-8 text-right flex-shrink-0" data-id="element-10058">
                    {src.percent}%
                  </span>
                </div>)}
            </div>
          </Card>;
      case 'geography':
        return <Card padding="md" className="h-full" data-id="element-10059">
            <div className="flex items-center justify-between mb-6" data-id="element-10060">
              <div className="flex items-center gap-2" data-id="element-10061">
                <div data-id="element-10062">
                  <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5" data-id="element-10063">
                    География
                    <div className="group relative" data-id="element-10064">
                      <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" data-id="element-10065" />
                      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-slate-800 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center" data-id="element-10066">
                        Данные собираются автоматически на основе IP-адресов
                        посетителей и данных из заказов.
                        <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-slate-800" data-id="element-10067" />
                      </div>
                    </div>
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-10068">Топ городов</p>
                </div>
              </div>
              <MapPin className="w-4 h-4 text-slate-400" data-id="element-10069" />
            </div>
            <div className="space-y-3" data-id="element-10070">
              {geography.map((geo, i) => <div key={i} className="flex items-center justify-between text-sm" data-id="element-10071">
                  <div className="flex items-center gap-2" data-id="element-10072">
                    <span className="text-slate-400 text-xs w-4" data-id="element-10073">{i + 1}.</span>
                    <span className="font-medium text-slate-700" data-id="element-10074">
                      {geo.city}
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-10075">
                    <span className="text-slate-500 text-xs" data-id="element-10076">
                      {geo.users.toLocaleString('ru-RU')}
                    </span>
                    <span className="text-xs font-bold text-slate-900 w-8 text-right" data-id="element-10077">
                      {geo.percent}%
                    </span>
                  </div>
                </div>)}
            </div>
          </Card>;
      case 'services':
        return <Card padding="md" className="h-full" data-id="element-10078">
            <div className="flex items-center justify-between mb-6" data-id="element-10079">
              <div data-id="element-10080">
                <h3 className="text-sm font-bold text-slate-900" data-id="element-10081">
                  Заказы по услугам
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-10082">
                  Распределение по типам
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setActiveTab('services')} data-id="element-10083">
                Все услуги
              </Button>
            </div>
            <div className="space-y-4" data-id="element-10084">
              {Object.entries(serviceBreakdown).sort(([, a], [, b]) => b - a).map(([service, count], i) => {
              const colors = ['bg-indigo-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-violet-500'];
              return <div key={service} data-id="element-10085">
                      <div className="flex justify-between text-xs mb-1.5" data-id="element-10086">
                        <span className="font-medium text-slate-700" data-id="element-10087">
                          {service}
                        </span>
                        <span className="text-slate-500" data-id="element-10088">
                          {count} ({Math.round(count / totalOrders * 100)}%)
                        </span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-10089">
                        <div className={`h-full ${colors[i % colors.length]} rounded-full transition-all`} style={{
                    width: `${count / totalOrders * 100}%`
                  }} data-id="element-10090" />
                      </div>
                    </div>;
            })}
            </div>
          </Card>;
      case 'subscriptions':
        return <Card padding="md" className="h-full" data-id="element-10091">
            <div className="flex items-center justify-between mb-6" data-id="element-10092">
              <div data-id="element-10093">
                <h3 className="text-sm font-bold text-slate-900" data-id="element-10094">Подписки</h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-10095">
                  Распределение пользователей
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setActiveTab('subscriptions')} data-id="element-10096">
                Управление
              </Button>
            </div>
            <div className="space-y-5" data-id="element-10097">
              <div data-id="element-10098">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-10099">
                  <span className="font-medium text-slate-700" data-id="element-10100">Pro тариф</span>
                  <span className="text-slate-500" data-id="element-10101">
                    {proUsers} ({Math.round(proUsers / users.length * 100)}%)
                  </span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-10102">
                  <div className="h-full bg-indigo-500 rounded-full transition-all" style={{
                  width: `${proUsers / users.length * 100}%`
                }} data-id="element-10103" />
                </div>
              </div>
              <div data-id="element-10104">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-10105">
                  <span className="font-medium text-slate-700" data-id="element-10106">Plus тариф</span>
                  <span className="text-slate-500" data-id="element-10107">
                    {plusUsers} ({Math.round(plusUsers / users.length * 100)}
                    %)
                  </span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-10108">
                  <div className="h-full bg-amber-500 rounded-full transition-all" style={{
                  width: `${plusUsers / users.length * 100}%`
                }} data-id="element-10109" />
                </div>
              </div>
              <div data-id="element-10110">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-10111">
                  <span className="font-medium text-slate-700" data-id="element-10112">Бесплатный</span>
                  <span className="text-slate-500" data-id="element-10113">
                    {freeUsers} ({Math.round(freeUsers / users.length * 100)}
                    %)
                  </span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-10114">
                  <div className="h-full bg-slate-400 rounded-full transition-all" style={{
                  width: `${freeUsers / users.length * 100}%`
                }} data-id="element-10115" />
                </div>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-slate-100" data-id="element-10116">
              <div className="flex justify-between text-xs" data-id="element-10117">
                <span className="text-slate-500" data-id="element-10118">Средний чек (LTV)</span>
                <span className="font-bold text-slate-900" data-id="element-10119">
                  {avgOrderValue.toLocaleString('ru-RU')} ₽
                </span>
              </div>
            </div>
          </Card>;
      case 'astrologers':
        return <Card padding="none" className="overflow-hidden h-full" data-id="element-10120">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50" data-id="element-10121">
              <div data-id="element-10122">
                <h3 className="text-sm font-bold text-slate-900" data-id="element-10123">
                  Топ астрологов
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-10124">
                  Лидеры по количеству заказов
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setActiveTab('astrologers')} data-id="element-10125">
                Каталог астрологов <ArrowRight className="w-3 h-3 ml-1.5" data-id="element-10126" />
              </Button>
            </div>
            <div className="overflow-x-auto" data-id="element-10127">
              <table className="w-full text-left" data-id="element-10128">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 tracking-wider border-b border-slate-100" data-id="element-10129">
                  <tr data-id="element-10130">
                    <th className="px-5 py-3 font-medium" data-id="element-10131">#</th>
                    <th className="px-5 py-3 font-medium" data-id="element-10132">Астролог</th>
                    <th className="px-5 py-3 font-medium" data-id="element-10133">Специализация</th>
                    <th className="px-5 py-3 font-medium" data-id="element-10134">Рейтинг</th>
                    <th className="px-5 py-3 font-medium" data-id="element-10135">Заказов</th>
                    <th className="px-5 py-3 font-medium" data-id="element-10136">Выручка</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-10137">
                  {topAstrologersList.map((a, i) => <tr key={a.id} className="hover:bg-slate-50 transition-colors" data-id="element-10138">
                      <td className="px-5 py-3" data-id="element-10139">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-slate-200 text-slate-600' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-10140">
                          {i + 1}
                        </span>
                      </td>
                      <td className="px-5 py-3" data-id="element-10141">
                        <div className="flex items-center gap-3" data-id="element-10142">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700" data-id="element-10143">
                            {a.name.split(' ').map((n: string) => n[0]).join('')}
                          </div>
                          <div data-id="element-10144">
                            <div className="text-sm font-medium text-slate-900" data-id="element-10145">
                              {a.name}
                            </div>
                            {a.badge && <Badge variant={a.badge.toLowerCase() === 'top' ? 'top' : 'pro'} size="sm" className="mt-1" data-id="element-10146">
                                {a.badge}
                              </Badge>}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-sm text-slate-500" data-id="element-10147">
                        {a.spec}
                      </td>
                      <td className="px-5 py-3" data-id="element-10148">
                        <div className="flex items-center gap-1.5" data-id="element-10149">
                          <Star className="w-4 h-4 text-amber-400 fill-amber-400" data-id="element-10150" />
                          <span className="text-sm font-medium text-slate-700" data-id="element-10151">
                            {a.rating}
                          </span>
                          <span className="text-xs text-slate-400" data-id="element-10152">
                            ({a.reviews})
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-sm font-medium text-slate-900" data-id="element-10153">
                        {a.orders}
                      </td>
                      <td className="px-5 py-3 text-sm font-medium text-emerald-600" data-id="element-10154">
                        {a.earnings}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </Card>;
      case 'realtime':
        return <Card padding="md" className="h-full flex flex-col justify-center items-center text-center relative overflow-hidden" data-id="element-10155">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-white" data-id="element-10156" />
            <div className="relative z-10" data-id="element-10157">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3" data-id="element-10158">
                <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" data-id="element-10159" />
              </div>
              <div className="text-4xl font-black text-slate-900 mb-1" data-id="element-10160">
                {onlineNow}
              </div>
              <div className="text-xs text-slate-500 font-medium flex items-center justify-center gap-1" data-id="element-10161">
                Пользователей онлайн
                <div className="group relative" data-id="element-10162">
                  <Info className="w-3 h-3 text-slate-400 cursor-help" data-id="element-10163" />
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-44 p-2 bg-slate-800 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center" data-id="element-10164">
                    Обновляется в реальном времени на основе активных сессий из
                    счётчиков.
                    <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-slate-800" data-id="element-10165" />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-center gap-3 mt-4 text-[10px]" data-id="element-10166">
                <div className="flex items-center gap-1 text-slate-500" data-id="element-10167">
                  <Eye className="w-3 h-3" data-id="element-10168" /> {Math.round(onlineNow * 2.3)}{' '}
                  просмотров/мин
                </div>
              </div>
              <div className="mt-3 flex items-center justify-center gap-1.5" data-id="element-10169">
                {[...Array(8)].map((_, i) => <div key={i} className={`w-1 rounded-full bg-emerald-400 transition-all`} style={{
                height: `${8 + Math.random() * 16}px`,
                opacity: 0.4 + Math.random() * 0.6
              }} data-id="element-10170" />)}
              </div>
            </div>
          </Card>;
      case 'alerts':
        return <Card padding="md" className="h-full" data-id="element-10171">
            <div className="flex items-center justify-between mb-4" data-id="element-10172">
              <div data-id="element-10173">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5" data-id="element-10174">
                  <Bell className="w-4 h-4 text-slate-400" data-id="element-10175" /> Уведомления
                  <div className="group relative" data-id="element-10176">
                    <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" data-id="element-10177" />
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-slate-800 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center" data-id="element-10178">
                      Алерты генерируются автоматически при обнаружении аномалий
                      в метриках и важных событиях.
                      <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-slate-800" data-id="element-10179" />
                    </div>
                  </div>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-10180">
                  Важные события и аномалии
                </p>
              </div>
              <Badge variant="warning" size="sm" data-id="element-10181">
                {alertsData.length} новых
              </Badge>
            </div>
            <div className="space-y-2.5" data-id="element-10182">
              {alertsData.map((alert, i) => {
              const IconComp = alert.icon;
              return <div key={i} className={`flex items-start gap-3 p-3 rounded-xl ${alert.bg} border ${alert.border}`} data-id="element-10183">
                    <IconComp className={`w-4 h-4 ${alert.color} flex-shrink-0 mt-0.5`} data-id="element-10184" />
                    <div className="flex-1 min-w-0" data-id="element-10185">
                      <div className="text-xs font-bold text-slate-900" data-id="element-10186">
                        {alert.title}
                      </div>
                      <div className="text-[10px] text-slate-600 mt-0.5 line-clamp-2" data-id="element-10187">
                        {alert.desc}
                      </div>
                    </div>
                    <span className="text-[9px] text-slate-400 flex-shrink-0 whitespace-nowrap" data-id="element-10188">
                      {alert.time}
                    </span>
                  </div>;
            })}
            </div>
          </Card>;
      case 'devices':
        return <Card padding="md" className="h-full" data-id="element-10189">
            <div className="flex items-center justify-between mb-5" data-id="element-10190">
              <div data-id="element-10191">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5" data-id="element-10192">
                  Устройства
                  <div className="group relative" data-id="element-10193">
                    <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" data-id="element-10194" />
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-slate-800 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center" data-id="element-10195">
                      Данные из Яндекс.Метрики / Google Analytics. Требуется
                      подключение счётчика.
                      <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-slate-800" data-id="element-10196" />
                    </div>
                  </div>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-10197">Типы устройств</p>
              </div>
              <Smartphone className="w-4 h-4 text-slate-400" data-id="element-10198" />
            </div>
            {/* CSS Donut */}
            <div className="flex justify-center mb-5" data-id="element-10199">
              <div className="w-28 h-28 rounded-full relative" style={{
              background: `conic-gradient(#3b82f6 0% ${deviceData[0].percent}%, #6366f1 ${deviceData[0].percent}% ${deviceData[0].percent + deviceData[1].percent}%, #f59e0b ${deviceData[0].percent + deviceData[1].percent}% 100%)`
            }} data-id="element-10200">
                <div className="absolute inset-3 bg-white rounded-full flex items-center justify-center" data-id="element-10201">
                  <div className="text-center" data-id="element-10202">
                    <div className="text-lg font-black text-slate-900" data-id="element-10203">
                      {deviceData[0].percent}%
                    </div>
                    <div className="text-[9px] text-slate-500" data-id="element-10204">Mobile</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-2.5" data-id="element-10205">
              {deviceData.map((d, i) => {
              const IconComp = d.icon;
              return <div key={i} className="flex items-center justify-between" data-id="element-10206">
                    <div className="flex items-center gap-2" data-id="element-10207">
                      <div className={`w-6 h-6 rounded-md ${d.bgColor} flex items-center justify-center`} data-id="element-10208">
                        <IconComp className={`w-3.5 h-3.5 ${d.textColor}`} data-id="element-10209" />
                      </div>
                      <span className="text-xs font-medium text-slate-700" data-id="element-10210">
                        {d.device}
                      </span>
                    </div>
                    <span className="text-xs font-bold text-slate-900" data-id="element-10211">
                      {d.percent}%
                    </span>
                  </div>;
            })}
            </div>
          </Card>;
      case 'popular_pages':
        return <Card padding="md" className="h-full" data-id="element-10212">
            <div className="flex items-center justify-between mb-5" data-id="element-10213">
              <div data-id="element-10214">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5" data-id="element-10215">
                  Популярные страницы
                  <div className="group relative" data-id="element-10216">
                    <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" data-id="element-10217" />
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-slate-800 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center" data-id="element-10218">
                      Данные из подключенных счётчиков аналитики (Яндекс.Метрика
                      / GA).
                      <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-slate-800" data-id="element-10219" />
                    </div>
                  </div>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-10220">
                  Топ по просмотрам за период
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setActiveTab('content')} data-id="element-10221">
                SEO-отчёт
              </Button>
            </div>
            <div className="space-y-3" data-id="element-10222">
              {popularPages.map((p, i) => <div key={i} className="flex items-center gap-3" data-id="element-10223">
                  <span className="text-slate-400 text-xs w-4 flex-shrink-0" data-id="element-10224">
                    {i + 1}.
                  </span>
                  <div className="flex-1 min-w-0" data-id="element-10225">
                    <div className="flex justify-between text-xs mb-1" data-id="element-10226">
                      <span className="font-medium text-slate-700 truncate" data-id="element-10227">
                        {p.page}
                      </span>
                      <span className="text-slate-500 ml-2 flex-shrink-0" data-id="element-10228">
                        {p.views.toLocaleString('ru-RU')}
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-10229">
                      <div className="h-full bg-indigo-400 rounded-full transition-all duration-700" style={{
                    width: `${p.percent}%`
                  }} data-id="element-10230" />
                    </div>
                  </div>
                </div>)}
            </div>
          </Card>;
      case 'content':
        return <Card padding="md" className="h-full" data-id="element-10231">
            <div className="flex items-center justify-between mb-5" data-id="element-10232">
              <div data-id="element-10233">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5" data-id="element-10234">
                  Контент-аналитика
                  <div className="group relative" data-id="element-10235">
                    <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" data-id="element-10236" />
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-slate-800 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center" data-id="element-10237">
                      Данные из внутренней CMS и счётчиков аналитики. Просмотры
                      и комментарии обновляются автоматически.
                      <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-slate-800" data-id="element-10238" />
                    </div>
                  </div>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-10239">
                  Статистика блога
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setActiveTab('content')} data-id="element-10240">
                Управление блогом
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-5" data-id="element-10241">
              <div className="bg-slate-50 rounded-xl p-3 text-center" data-id="element-10242">
                <div className="text-lg font-bold text-slate-900" data-id="element-10243">
                  {(blogStats.totalViews / 1000).toFixed(1)}K
                </div>
                <div className="text-[10px] text-slate-500" data-id="element-10244">Просмотров</div>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 text-center" data-id="element-10245">
                <div className="text-lg font-bold text-slate-900" data-id="element-10246">
                  {blogStats.avgReadTime}
                </div>
                <div className="text-[10px] text-slate-500" data-id="element-10247">Ср. чтение</div>
              </div>
            </div>
            <div className="space-y-3" data-id="element-10248">
              {blogStats.topArticles.map((article, i) => <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0" data-id="element-10249">
                  <div className="flex-1 min-w-0 mr-3" data-id="element-10250">
                    <div className="text-xs font-medium text-slate-700 truncate" data-id="element-10251">
                      {article.title}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-2" data-id="element-10252">
                      <span className="flex items-center gap-0.5" data-id="element-10253">
                        <Eye className="w-3 h-3" data-id="element-10254" />{' '}
                        {article.views.toLocaleString('ru-RU')}
                      </span>
                      <span className="flex items-center gap-0.5" data-id="element-10255">
                        <MessageCircle className="w-3 h-3" data-id="element-10256" /> {article.comments}
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </Card>;
      case 'reviews_analytics':
        return <Card padding="md" className="h-full" data-id="element-10257">
            <div className="flex items-center justify-between mb-5" data-id="element-10258">
              <div data-id="element-10259">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5" data-id="element-10260">
                  Отзывы и рейтинг
                  <div className="group relative" data-id="element-10261">
                    <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" data-id="element-10262" />
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-slate-800 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center" data-id="element-10263">
                      Данные из отзывов пользователей на платформе. Рейтинг
                      обновляется автоматически.
                      <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-slate-800" data-id="element-10264" />
                    </div>
                  </div>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-10265">
                  Обратная связь пользователей
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setActiveTab('moderation')} data-id="element-10266">
                Модерация
              </Button>
            </div>
            <div className="flex items-center gap-4 mb-5" data-id="element-10267">
              <div className="text-center" data-id="element-10268">
                <div className="text-3xl font-black text-slate-900" data-id="element-10269">
                  {reviewsData.avgRating}
                </div>
                <div className="flex gap-0.5 justify-center mt-1" data-id="element-10270">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(reviewsData.avgRating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-10271" />)}
                </div>
                <div className="text-[10px] text-slate-500 mt-1" data-id="element-10272">
                  {reviewsData.totalReviews.toLocaleString('ru-RU')} отзывов
                </div>
              </div>
              <div className="flex-1 space-y-1.5" data-id="element-10273">
                {reviewsData.distribution.map((d, i) => <div key={i} className="flex items-center gap-2" data-id="element-10274">
                    <span className="text-[10px] text-slate-500 w-3" data-id="element-10275">
                      {d.stars}
                    </span>
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-10276">
                      <div className="h-full bg-amber-400 rounded-full" style={{
                    width: `${d.percent}%`
                  }} data-id="element-10277" />
                    </div>
                    <span className="text-[10px] text-slate-500 w-7 text-right" data-id="element-10278">
                      {d.percent}%
                    </span>
                  </div>)}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2" data-id="element-10279">
              <div className="bg-emerald-50 rounded-lg p-2 text-center" data-id="element-10280">
                <div className="text-sm font-bold text-emerald-700" data-id="element-10281">
                  {reviewsData.positive}%
                </div>
                <div className="text-[9px] text-emerald-600" data-id="element-10282">Позитивных</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2 text-center" data-id="element-10283">
                <div className="text-sm font-bold text-slate-700" data-id="element-10284">
                  {reviewsData.neutral}%
                </div>
                <div className="text-[9px] text-slate-500" data-id="element-10285">Нейтральных</div>
              </div>
              <div className="bg-rose-50 rounded-lg p-2 text-center" data-id="element-10286">
                <div className="text-sm font-bold text-rose-700" data-id="element-10287">
                  {reviewsData.negative}%
                </div>
                <div className="text-[9px] text-rose-600" data-id="element-10288">Негативных</div>
              </div>
            </div>
          </Card>;
      case 'email':
        return <Card padding="md" className="h-full" data-id="element-10289">
            <div className="flex items-center justify-between mb-5" data-id="element-10290">
              <div data-id="element-10291">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5" data-id="element-10292">
                  <Mail className="w-4 h-4 text-slate-400" data-id="element-10293" /> Email-рассылки
                  <div className="group relative" data-id="element-10294">
                    <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" data-id="element-10295" />
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-slate-800 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center" data-id="element-10296">
                      Данные из сервиса email-рассылок. Статистика обновляется
                      после каждой отправки.
                      <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-slate-800" data-id="element-10297" />
                    </div>
                  </div>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-10298">
                  Статистика кампаний
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setActiveTab('marketing')} data-id="element-10299">
                Рассылки
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-5" data-id="element-10300">
              <div className="bg-blue-50 rounded-xl p-3" data-id="element-10301">
                <div className="text-lg font-bold text-blue-700" data-id="element-10302">
                  {emailStats.openRate}%
                </div>
                <div className="text-[10px] text-blue-600" data-id="element-10303">Открытия</div>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3" data-id="element-10304">
                <div className="text-lg font-bold text-emerald-700" data-id="element-10305">
                  {emailStats.clickRate}%
                </div>
                <div className="text-[10px] text-emerald-600" data-id="element-10306">Клики</div>
              </div>
            </div>
            <div className="space-y-3 mb-4" data-id="element-10307">
              {emailStats.campaigns.map((c, i) => <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0" data-id="element-10308">
                  <span className="text-xs font-medium text-slate-700 truncate flex-1 mr-2" data-id="element-10309">
                    {c.name}
                  </span>
                  <div className="flex items-center gap-3 text-[10px] flex-shrink-0" data-id="element-10310">
                    <span className="text-blue-600 font-medium" data-id="element-10311">
                      {c.openRate}% откр.
                    </span>
                    <span className="text-emerald-600 font-medium" data-id="element-10312">
                      {c.clickRate}% клик
                    </span>
                  </div>
                </div>)}
            </div>
            <div className="flex items-center justify-between text-[10px] text-slate-400 pt-3 border-t border-slate-100" data-id="element-10313">
              <span data-id="element-10314">Отправлено: {emailStats.sent.toLocaleString('ru-RU')}</span>
              <span data-id="element-10315">
                Отписки: {emailStats.unsubscribed.toLocaleString('ru-RU')}
              </span>
            </div>
          </Card>;
      case 'cac':
        return <Card padding="md" className="h-full" data-id="element-10316">
            <div className="flex items-center justify-between mb-5" data-id="element-10317">
              <div data-id="element-10318">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5" data-id="element-10319">
                  Стоимость привлечения
                  <div className="group relative" data-id="element-10320">
                    <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" data-id="element-10321" />
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-slate-800 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center" data-id="element-10322">
                      CAC — средняя стоимость привлечения одного платящего
                      клиента через каждый канал.
                      <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-slate-800" data-id="element-10323" />
                    </div>
                  </div>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-10324">
                  Средний CAC:{' '}
                  <span className="font-bold text-slate-900" data-id="element-10325">{avgCAC} ₽</span>
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setActiveTab('marketing')} data-id="element-10326">
                Маркетинг
              </Button>
            </div>
            <div className="space-y-3" data-id="element-10327">
              {cacData.map((c, i) => <div key={i} className="flex items-center gap-3" data-id="element-10328">
                  <div className={`w-2 h-2 rounded-full ${c.color} flex-shrink-0`} data-id="element-10329" />
                  <div className="flex-1 min-w-0" data-id="element-10330">
                    <div className="flex justify-between text-xs mb-0.5" data-id="element-10331">
                      <span className="font-medium text-slate-700 truncate" data-id="element-10332">
                        {c.channel}
                      </span>
                      <span className={`font-bold flex-shrink-0 ml-2 ${c.cac === 0 ? 'text-emerald-600' : c.cac > 500 ? 'text-rose-600' : 'text-slate-900'}`} data-id="element-10333">
                        {c.cac === 0 ? 'Бесплатно' : `${c.cac} ₽`}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-400" data-id="element-10334">
                      {c.orders} заказов
                    </div>
                  </div>
                </div>)}
            </div>
          </Card>;
      case 'metrics':
        return <div className={`grid gap-4 h-full ${widget.size === 'full' ? 'grid-cols-2 lg:grid-cols-4' : widget.size === '3/4' ? 'grid-cols-2 lg:grid-cols-3' : widget.size === '1/2' ? 'grid-cols-2' : 'grid-cols-1'}`} data-id="element-10335">
            <Card padding="md" className="text-center hover:border-indigo-200 transition-colors h-full flex flex-col justify-center" data-id="element-10336">
              <div className="text-xs text-slate-500 mb-1" data-id="element-10337">
                Ср. время на сайте
              </div>
              <div className="text-xl font-bold text-slate-900" data-id="element-10338">4:32</div>
              <div className="text-[10px] text-emerald-600 font-medium mt-1" data-id="element-10339">
                +12% к пр. периоду
              </div>
            </Card>
            <Card padding="md" className="text-center hover:border-indigo-200 transition-colors h-full flex flex-col justify-center" data-id="element-10340">
              <div className="text-xs text-slate-500 mb-1" data-id="element-10341">
                Показатель отказов
              </div>
              <div className="text-xl font-bold text-slate-900" data-id="element-10342">24.8%</div>
              <div className="text-[10px] text-emerald-600 font-medium mt-1" data-id="element-10343">
                -3.2% к пр. периоду
              </div>
            </Card>
            <Card padding="md" className="text-center hover:border-indigo-200 transition-colors h-full flex flex-col justify-center" data-id="element-10344">
              <div className="text-xs text-slate-500 mb-1" data-id="element-10345">
                Повторные покупки
              </div>
              <div className="text-xl font-bold text-slate-900" data-id="element-10346">38%</div>
              <div className="text-[10px] text-emerald-600 font-medium mt-1" data-id="element-10347">
                +5.1% к пр. периоду
              </div>
            </Card>
            <Card padding="md" className="text-center hover:border-indigo-200 transition-colors h-full flex flex-col justify-center" data-id="element-10348">
              <div className="text-xs text-slate-500 mb-1" data-id="element-10349">
                NPS (Лояльность)
              </div>
              <div className="text-xl font-bold text-slate-900" data-id="element-10350">72</div>
              <div className="text-[10px] text-emerald-600 font-medium mt-1" data-id="element-10351">
                +4 к пр. периоду
              </div>
            </Card>
          </div>;
      default:
        return null;
    }
  };
  // Получаем видимые и скрытые виджеты
  const visibleWidgets = layout.filter(w => w.visible);
  const hiddenWidgets = layout.filter(w => !w.visible);
  return <div className="space-y-6" data-id="element-10352">
      {/* Header & Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm sticky top-0 z-40" data-id="element-10353">
        <div data-id="element-10354">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-3" data-id="element-10355">
            Аналитика платформы
            <Badge variant={isEditing ? 'warning' : 'neutral'} size="sm" className="font-normal" data-id="element-10356">
              {isEditing ? 'Режим редактирования' : 'Обзор'}
            </Badge>
          </h2>
          <p className="text-sm text-slate-500" data-id="element-10357">
            Обзор ключевых показателей и метрик
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-10358">
          {!isEditing && <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl mr-2" data-id="element-10359">
              {[{
            id: '7d',
            label: '7 дней'
          }, {
            id: '30d',
            label: '30 дней'
          }, {
            id: '3m',
            label: '3 месяца'
          }, {
            id: '1y',
            label: 'Год'
          }].map(p => <button key={p.id} onClick={() => setPeriod(p.id)} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${period === p.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'}`} data-id="element-10360">
                  {p.label}
                </button>)}
            </div>}

          {isEditing && <Button variant="outline" size="sm" onClick={resetLayout} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-id="element-10361">
              Сбросить макет
            </Button>}
          <Button variant={isEditing ? 'primary' : 'outline'} size="sm" onClick={() => {
          if (isEditing) {
            success('Макет дашборда сохранен');
          }
          setIsEditing(!isEditing);
        }} data-id="element-10362">
            {isEditing ? <>
                <Save className="w-4 h-4 mr-2" data-id="element-10363" /> Сохранить макет
              </> : <>
                <Settings2 className="w-4 h-4 mr-2" data-id="element-10364" /> Настроить дашборд
              </>}
          </Button>
        </div>
      </div>

      {/* Панель скрытых виджетов в режиме редактирования */}
      {isEditing && hiddenWidgets.length > 0 && <div className="bg-slate-50 border border-slate-200 border-dashed rounded-xl p-4" data-id="element-10365">
          <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2" data-id="element-10366">
            <EyeOff className="w-4 h-4" data-id="element-10367" /> Скрытые виджеты
          </h3>
          <div className="flex flex-wrap gap-2" data-id="element-10368">
            {hiddenWidgets.map(w => {
          const config = WIDGETS_CONFIG.find(c => c.id === w.id);
          return <button key={w.id} onClick={() => toggleVisibility(w.id)} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 hover:border-indigo-300 hover:text-indigo-600 transition-colors shadow-sm" data-id="element-10369">
                  <Plus className="w-3.5 h-3.5" data-id="element-10370" />
                  {config?.title}
                </button>;
        })}
          </div>
        </div>}

      {/* Рендер макета (Grid-система) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-10371">
        {visibleWidgets.map((widget, index) => <WidgetWrapper key={widget.id} widget={widget} index={index} data-id="element-10372">
            {renderWidget(widget)}
          </WidgetWrapper>)}
      </div>

      {/* Пустое состояние, если всё скрыто */}
      {visibleWidgets.length === 0 && !isEditing && <div className="text-center py-20 bg-white rounded-2xl border border-slate-200 border-dashed" data-id="element-10373">
          <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" data-id="element-10374" />
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-10375">
            Дашборд пуст
          </h3>
          <p className="text-slate-500 mb-6" data-id="element-10376">Все виджеты аналитики скрыты.</p>
          <Button onClick={() => setIsEditing(true)} data-id="element-10377">Настроить дашборд</Button>
        </div>}
    </div>;
}